import { MoreVertical } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface ActionMenuProps {
  actions: ActionMenuItem[];
  buttonClassName?: string;
  menuClassName?: string;
  icon?: React.ReactNode;
  ariaLabel?: string;
}

export default function ActionMenu({
  actions,
  buttonClassName = '',
  menuClassName = '',
  icon = <MoreVertical className="h-6 w-6 text-muted-1 transition-all duration-300 group-hover/btn:text-primary-1 group-hover/btn:scale-110" />,
  ariaLabel = 'Open actions menu',
}: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return undefined;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Keyboard navigation: close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Detect if menu should open upwards
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 120; // Approximate, or you can measure dynamically
      if (spaceBelow < menuHeight) {
        setOpenUpwards(true);
      } else {
        setOpenUpwards(false);
      }
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        ref={buttonRef}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className={`rounded-md p-2 focus:text-primary-1 focus:outline-none group/btn ${buttonClassName}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }}
      >
        {icon}
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          className={`absolute right-0 ${openUpwards ? 'bottom-full mb-2' : 'top-full mt-2'} z-50 w-fit min-w-[150px] overflow-hidden rounded-md border border-muted-3 bg-primary-2 shadow-lg focus:outline-none ${menuClassName}`}
          role="menu"
        >
          {actions.map((action, idx) => (
            <button
              key={action.label}
              type="button"
              className={`flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors duration-150 hover:bg-muted-5 focus:bg-muted-5 focus:outline-none ${
                action.danger ? 'text-red-500' : 'text-primary-1'
              } ${action.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={(e) => {
                if (!action.disabled) {
                  e.stopPropagation();
                  e.preventDefault();
                  action.onClick();
                  setIsOpen(false);
                }
              }}
              disabled={action.disabled}
              role="menuitem"
              tabIndex={0}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
